// src/features/sessions/components/RemoteSessionsPage.tsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Tooltip,
  IconButton,
  LinearProgress,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Stop as StopIcon,
  OpenInNew as OpenIcon,
  Refresh as RefreshIcon,
  Timer as TimerIcon,
  Cloud as CloudIcon,
  ContentCopy as CopyIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Schedule as ExtendIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import {
  createRemoteSession,
  listRemoteSessions,
  getRemoteSession,
  terminateRemoteSession,
  extendRemoteSession,
  RemoteSession,
} from '../api/sessionsApi';

const STATUS_COLORS: Record<string, 'info' | 'success' | 'warning' | 'error' | 'default'> = {
  provisioning: 'info',
  running: 'success',
  stopping: 'warning',
  stopped: 'default',
  failed: 'error',
};

const RemoteSessionsPage: React.FC = () => {
  const [sessions, setSessions] = useState<RemoteSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [gitRepo, setGitRepo] = useState('');
  const [copied, setCopied] = useState<string | null>(null);

  const fetchSessions = useCallback(async () => {
    try {
      const result = await listRemoteSessions();
      setSessions(result.sessions || []);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    // Auto-refresh every 15 seconds (for provisioning status updates)
    const interval = setInterval(fetchSessions, 15000);
    return () => clearInterval(interval);
  }, [fetchSessions]);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const result = await createRemoteSession({
        gitRepo: gitRepo.trim() || undefined,
      });
      setShowCreateDialog(false);
      setGitRepo('');

      // If the session got an access URL immediately, open it
      if (result.session.accessUrl) {
        window.open(result.session.accessUrl, '_blank');
      }

      await fetchSessions();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to create session');
    } finally {
      setCreating(false);
    }
  };

  const handleTerminate = async (id: string) => {
    try {
      await terminateRemoteSession(id);
      await fetchSessions();
    } catch (err: any) {
      setError(err.message || 'Failed to terminate session');
    }
  };

  const handleExtend = async (id: string) => {
    try {
      await extendRemoteSession(id, 3600);
      await fetchSessions();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to extend session');
    }
  };

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleOpen = async (session: RemoteSession) => {
    if (session.accessUrl) {
      window.open(session.accessUrl, '_blank');
    } else if (session.status === 'provisioning') {
      // Poll for the session to become ready
      try {
        const result = await getRemoteSession(session.id);
        if (result.session.accessUrl) {
          window.open(result.session.accessUrl, '_blank');
        }
      } catch {}
    }
  };

  const activeSessions = sessions.filter(s => ['provisioning', 'running'].includes(s.status));
  const pastSessions = sessions.filter(s => ['stopped', 'failed'].includes(s.status));

  const getTimeRemaining = (expiresAt: string) => {
    const remaining = new Date(expiresAt).getTime() - Date.now();
    if (remaining <= 0) return 'Expired';
    const minutes = Math.floor(remaining / 60000);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) return `${hours}h ${minutes % 60}m remaining`;
    return `${minutes}m remaining`;
  };

  const getProgressPercent = (session: RemoteSession) => {
    if (!session.startedAt) return 0;
    const start = new Date(session.startedAt).getTime();
    const end = new Date(session.expiresAt).getTime();
    const now = Date.now();
    return Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));
  };

  return (
    <Box sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudIcon color="primary" />
            Remote Sessions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Launch a cloud-hosted Loxia workspace accessible from any browser
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <IconButton onClick={fetchSessions} size="small">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateDialog(true)}
            disabled={creating}
          >
            New Session
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Active Sessions */}
      {!loading && activeSessions.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Active Sessions</Typography>
          <Stack spacing={2}>
            {activeSessions.map(session => (
              <Card key={session.id} variant="outlined" sx={{ borderColor: 'success.light' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={session.status}
                          color={STATUS_COLORS[session.status]}
                          size="small"
                        />
                        {session.status === 'provisioning' && (
                          <CircularProgress size={16} />
                        )}
                      </Stack>
                      {session.config?.gitRepo && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Repo: {session.config.gitRepo}
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TimerIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {getTimeRemaining(session.expiresAt)}
                      </Typography>
                    </Stack>
                  </Box>

                  {/* Time remaining bar */}
                  {session.status === 'running' && (
                    <LinearProgress
                      variant="determinate"
                      value={getProgressPercent(session)}
                      color={getProgressPercent(session) > 80 ? 'warning' : 'primary'}
                      sx={{ mt: 2, mb: 1, borderRadius: 1 }}
                    />
                  )}

                  {/* Access URL */}
                  {session.fqdn && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {session.fqdn}:8080
                      </Typography>
                      <Tooltip title={copied === session.id ? 'Copied!' : 'Copy URL'}>
                        <IconButton size="small" onClick={() => handleCopy(session.accessUrl || '', session.id)}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<OpenIcon />}
                    onClick={() => handleOpen(session)}
                    disabled={session.status !== 'running'}
                  >
                    Open Workspace
                  </Button>
                  <Button
                    size="small"
                    startIcon={<ExtendIcon />}
                    onClick={() => handleExtend(session.id)}
                    disabled={session.status !== 'running'}
                  >
                    +1 Hour
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<StopIcon />}
                    onClick={() => handleTerminate(session.id)}
                  >
                    Terminate
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Stack>
        </Box>
      )}

      {/* Empty state */}
      {!loading && activeSessions.length === 0 && (
        <Paper
          variant="outlined"
          sx={{ p: 6, textAlign: 'center', mb: 4, borderStyle: 'dashed' }}
        >
          <CloudIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No active sessions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Launch a remote workspace to use Loxia from any browser. Sessions last 2 hours and are fully ephemeral.
          </Typography>
          <Button
            variant="contained"
            startIcon={<StartIcon />}
            onClick={() => setShowCreateDialog(true)}
          >
            Start New Session
          </Button>
        </Paper>
      )}

      {/* Past Sessions */}
      {!loading && pastSessions.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }} color="text.secondary">Recent Sessions</Typography>
          <Stack spacing={1}>
            {pastSessions.slice(0, 5).map(session => (
              <Paper key={session.id} variant="outlined" sx={{ p: 2, opacity: 0.7 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={session.status}
                      color={STATUS_COLORS[session.status]}
                      size="small"
                      variant="outlined"
                    />
                    {session.config?.gitRepo && (
                      <Typography variant="body2" color="text.secondary">
                        {session.config.gitRepo}
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {session.stoppedAt
                      ? `Ended ${formatDistanceToNow(new Date(session.stoppedAt), { addSuffix: true })}`
                      : `Created ${formatDistanceToNow(new Date(session.createdAt), { addSuffix: true })}`
                    }
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {/* Create Session Dialog */}
      <Dialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Start Remote Session</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            A cloud workspace will be provisioned with the full Loxia Autopilot environment.
            The session lasts 2 hours and is fully ephemeral — all data is lost when it ends.
          </Typography>

          <TextField
            fullWidth
            label="Git Repository URL (optional)"
            placeholder="https://github.com/user/project.git"
            value={gitRepo}
            onChange={e => setGitRepo(e.target.value)}
            helperText="If provided, the repository will be cloned into the workspace on startup."
            sx={{ mb: 2 }}
          />

          <Alert severity="info" sx={{ mt: 1 }}>
            Your API key will be automatically configured in the remote session.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={16} /> : <StartIcon />}
          >
            {creating ? 'Provisioning...' : 'Start Session'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RemoteSessionsPage;
